import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectTeam } from './hooks/useProjectTeam';
import ResourceSidebar from '../../shared/ResourceSidebar/ResourceSidebar';
import ProjectTeamList from './components/ProjectTeamList';
import ProjectPermissionEditor from './components/ProjectPermissionEditor';
import './ProjectTeam.css';

/**
 * @component ProjectTeamForm
 * @description Orchestrates the 3-column team management for projects.
 * Now using useProjectTeam hook for logic and state management.
 */
export default function ProjectTeamForm({ project, currentUser, tempTeamMembers = [], onTeamChange, readOnly, onClose, onNavigateAway, initialTeamTabs }) {
    const navigate = useNavigate();
    const {
        availableWorkers,
        availableVehicles,
        availableCategories,
        availableKolonnen,
        editingUser,
        teamView,
        activeTab,
        sidebarTab,
        userForm,
        assignedIds,
        displayList,
        setTeamView,
        setActiveTab,
        setSidebarTab,
        setEditingUser,
        setUserForm,
        handleUserSelect,
        handleToggleResource,
        handleRemoveUser,
        handleRemoveAllResources,
        setPermission,
        setJournalValue,
        handleSubmitPermissions,
        getWorkerStatus,
        getVehicleStatus
    } = useProjectTeam({ project, currentUser, tempTeamMembers, onTeamChange, readOnly, initialTeamTabs });

    const getInitials = (name) => {
        if (!name || name === 'Unbekannt') return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    const handleDoubleClick = (id, type) => {
        if (onNavigateAway) onNavigateAway({ teamView, activeTab, sidebarTab });
        if (onClose) onClose();
        
        if (type === 'worker') {
            navigate('/verwaltung/mitarbeiter', { state: { editWorkerId: id } });
        } else if (type === 'vehicle') {
            navigate('/verwaltung/fuhrpark', { state: { openVehicleId: id } });
        } else if (type === 'kolonne') {
            navigate('/verwaltung/mitarbeiter', { state: { targetTab: 'kolonnen', openKolonneId: id } });
        }
    };
    const handleManage = (tab) => {
        if (onNavigateAway) onNavigateAway({ teamView, activeTab, sidebarTab });
        if (onClose) onClose();

        if (tab === 'personal') {
            navigate('/verwaltung/mitarbeiter');
        } else if (tab === 'fahrzeuge') {
            navigate('/verwaltung/fuhrpark');
        } else if (tab === 'kolonnen') {
            navigate('/verwaltung/mitarbeiter', { state: { targetTab: 'kolonnen' } });
        }
    };

    return (
        <div className={`project-team-layout ${readOnly ? 'read-only-view' : ''}`}>
            <div className="team-resource-picker">
                <ResourceSidebar
                    isEmbedded={true}
                    workers={availableWorkers}
                    vehicles={availableVehicles}
                    categories={availableCategories}
                    kolonnen={availableKolonnen}
                    enabledTabs={teamView === 'blacklist' ? ['personal'] : ['personal', 'fahrzeuge', 'kolonnen']}
                    activeTab={teamView === 'blacklist' ? 'personal' : sidebarTab}
                    onTabChange={setSidebarTab}
                    assignedIds={assignedIds}
                    isEditMode={!readOnly}
                    onToggleResource={handleToggleResource}
                    isDraggable={false}
                    hideAssigned={true}
                    onDoubleClick={handleDoubleClick}
                    getWorkerStatus={getWorkerStatus}
                    getVehicleStatus={getVehicleStatus}
                    onManage={handleManage}
                />
            </div>

            <div className="team-assigned-list-wrapper">
                <ProjectTeamList 
                    displayList={displayList}
                    teamView={teamView}
                    setTeamView={setTeamView}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    editingUser={editingUser}
                    setEditingUser={setEditingUser}
                    currentUser={currentUser}
                    project={project}
                    handleUserSelect={handleUserSelect}
                    handleToggleResource={handleToggleResource}
                    handleRemoveAllResources={handleRemoveAllResources}
                    readOnly={readOnly}
                    availableWorkers={availableWorkers}
                    availableVehicles={availableVehicles}
                    onDoubleClick={handleDoubleClick}
                    getWorkerStatus={getWorkerStatus}
                    getVehicleStatus={getVehicleStatus}
                />
            </div>

            <div className="team-permissions-editor-wrapper">
                <ProjectPermissionEditor 
                    editingUser={editingUser}
                    userForm={userForm}
                    setUserForm={setUserForm}
                    setEditingUser={setEditingUser}
                    handleSubmitPermissions={handleSubmitPermissions}
                    handleRemoveUser={handleRemoveUser}
                    setPermission={setPermission}
                    setJournalValue={setJournalValue}
                    getInitials={getInitials}
                    readOnly={readOnly}
                    activeTab={activeTab}
                />
            </div>
        </div>
    );
}
